import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { debug } from '@/utils/logger';

interface ClipsContextValue {
  latestTikTok?: string;
  latestInstagram?: string;
  addLink: (link: string) => void;
  hasAddedLink: boolean;
}

const ClipsContext = createContext<ClipsContextValue | undefined>(undefined);

export const ClipsProvider = ({ children }: { children: ReactNode }) => {
  const [latestTikTok, setLatestTikTok] = useState<string | undefined>(
    undefined
  );
  const [latestInstagram, setLatestInstagram] = useState<string | undefined>(
    undefined
  );
  const [hasAddedLink, setHasAddedLink] = useState(false);

  const addLink = useCallback((link: string) => {
    debug("addLink called with:", link);

    if (/^https?:\/\/(www\.)?tiktok\.com\//i.test(link)) {
      debug("Detected TikTok link");
      const cleanUrl = link.split("?")[0];
      setLatestTikTok(cleanUrl);
      setLatestInstagram(undefined);
      setHasAddedLink(true);
    } else if (
      /^https?:\/\/(www\.)?instagram\.com\/(p\/|reel\/|tv\/)/i.test(link)
    ) {
      debug("Detected Instagram link");
      const cleanUrl = link.split("?")[0];
      setLatestInstagram(cleanUrl);
      setLatestTikTok(undefined);
      setHasAddedLink(true);
    } else {
      debug("No matching platform found for link:", link);
    }
  }, []);

  const value = useMemo(
    () => ({ latestTikTok, latestInstagram, addLink, hasAddedLink }),
    [latestTikTok, latestInstagram, addLink, hasAddedLink]
  );

  return (
    <ClipsContext.Provider value={value}>{children}</ClipsContext.Provider>
  );
};

export const useClips = (): ClipsContextValue => {
  const ctx = useContext(ClipsContext);
  if (!ctx) throw new Error("useClips must be used within ClipsProvider");
  return ctx;
};

export default ClipsContext;
